// src/pages/HomePage.jsx
import React, { useEffect, useState } from "react";
import HomeHeroSection from "../components/home/HomeHeroSection";
import { collection, onSnapshot, query, where, limit } from "firebase/firestore";
import { db } from "../services/firebase";

/**
 * Métadonnées par catégorie basées sur categorySlug des produits.
 * On n'a PAS de collection "categories", donc on utilise ceci pour
 * le nom + description + image par défaut.
 */
const CATEGORY_META = {
  "imprimantes-ticket": {
    label: "Imprimantes à ticket",
    description:
      "Imprimantes thermiques rapides pour vos caisses, restaurants, pharmacies et points de vente.",
    image: "/images/categories/imprimante-ticket.jpg",
  },
  "imprimantes-classiques": {
    label: "Imprimantes classiques",
    description:
      "Imprimantes A4 pour documents, rapports, factures, usage bureau ou professionnel.",
    image: "/images/categories/imprimante-classique.webp",
  },
  "rouleaux-papier": {
    label: "Rouleaux de papier",
    description:
      "Rouleaux thermiques pour imprimantes ticket, TPE, EPOS et terminaux de paiement.",
    image: "/images/categories/rouleaux-papier.jpg",
  },
  tpe: {
    label: "TPE (Terminaux de paiement)",
    description:
      "Terminaux de paiement pour encaisser par carte bancaire et mobile money en toute simplicité.",
    image: "/images/categories/tpe.jpg",
  },
  epos: {
    label: "EPOS / Caisses tactiles",
    description:
      "Systèmes de caisse complets : écran tactile, imprimante, tiroir-caisse et logiciels adaptés.",
    image: "/images/categories/epos.jpeg",
  },
  scanners: {
    label: "Scanners codes-barres",
    description:
      "Scanners 1D/2D pour lecture rapide de codes-barres en caisse ou en gestion de stock.",
    image: "/images/categories/scanner.jpg",
  },
  // 🆕 Nouvelle catégorie : Caisses manuelles
  "caisses-manuelles": {
    label: "Caisses manuelles",
    description:
      "Caisses à tiroir manuelles robustes pour boutiques, pharmacies et points d’encaissement.",
    image: "/images/categories/epos.jpeg",
  },
};

// petit helper pour formater les prix CFA
const formatPrice = (value) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
  }).format(value);

/* 🧱 Skeleton pour une carte catégorie */
function CategorySkeletonCard() {
  return (
    <div className="col">
      <div className="card h-100 border-0 shadow-sm small rounded-4 overflow-hidden bg-white">
        <div className="position-relative">
          <div className="ratio ratio-4x3 bg-light">
            <span className="placeholder w-100 h-100 d-block" />
          </div>
          <div className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-25 text-white text-center py-1">
            <span className="placeholder col-6" />
          </div>
        </div>
        <div className="card-body text-center d-flex flex-column">
          <div className="small mb-2 flex-grow-1">
            <div className="placeholder-glow mb-1">
              <span className="placeholder col-10" />
            </div>
            <div className="placeholder-glow mb-1">
              <span className="placeholder col-8" />
            </div>
          </div>
          <div className="d-flex justify-content-center">
            <span
              className="placeholder rounded-pill col-6"
              style={{ height: 28 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* 🧱 Skeleton pour une carte "Meilleure offre" */
function BestOfferSkeletonCard() {
  return (
    <div className="col">
      <div className="ps-product-card h-100 d-flex flex-column rounded-4 border-0 shadow-sm overflow-hidden bg-white">
        <div className="position-relative">
          <div className="ps-product-image bg-light">
            <span className="placeholder w-100 d-block" style={{ height: 140 }} />
          </div>
        </div>
        <div className="px-2 pb-2 pt-2 d-flex flex-column flex-grow-1">
          <div className="placeholder-glow mb-1">
            <span className="placeholder col-9" />
          </div>
          <div className="placeholder-glow mb-2">
            <span className="placeholder col-5" />
          </div>
          <div className="mt-auto">
            <div className="placeholder-glow mb-1">
              <span className="placeholder col-4" />
            </div>
            <div className="placeholder-glow">
              <span className="placeholder col-3" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  const [bestOffers, setBestOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // 🔗 Récupération des meilleures offres depuis Firestore
  useEffect(() => {
    const q = query(
      collection(db, "products_prosoft"),
      where("isFeatured", "==", true)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setBestOffers(list);
        setLoadingOffers(false);
      },
      (err) => {
        console.error("Erreur Firestore BEST_OFFERS :", err);
        setLoadingOffers(false);
      }
    );

    return () => unsub();
  }, []);

  // 🔥 Récupération DYNAMIQUE des catégories à partir des produits
  useEffect(() => {
    const q = collection(db, "products_prosoft");

    const unsub = onSnapshot(
      q,
      (snap) => {
        const grouped = {};

        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const slug = data.categorySlug;
          if (!slug) return;

          if (!grouped[slug]) {
            const meta = CATEGORY_META[slug] || {};
            const firstImage =
              data.mainImage ||
              (Array.isArray(data.images) && data.images[0]) ||
              null;

            grouped[slug] = {
              id: slug,
              slug,
              name: meta.label || slug,
              description:
                meta.description ||
                "Découvrez les équipements et accessoires disponibles dans cette catégorie.",
              image:
                meta.image ||
                firstImage ||
                "/images/categories/default-category.jpg",
              productCount: 0,
            };
          }

          // incrément du nombre de produits
          grouped[slug].productCount += 1;
        });

        setCategories(Object.values(grouped));
        setLoadingCategories(false);
      },
      (err) => {
        console.error(
          "Erreur Firestore CATEGORIES (via products_prosoft) :",
          err
        );
        setLoadingCategories(false);
      }
    );

    return () => unsub();
  }, []);

  return (
    <div className="home-page bg-white">
      {/* Tu pourras remettre ton Hero ici si besoin */}
      {/* <HomeHeroSection /> */}

      {/* ✅ Bloc "Produits par catégorie" */}
      {/* <section id="categories" className="py-4 border-top bg-white">
        <div className="container">
          <div className="text-center mb-3">
            <h5 className="fw-bold mb-1 text-uppercase">
              Matériels &amp; équipements
            </h5>
            <p className="text-muted small mb-0">
              Cliquez sur un type de matériel pour voir les produits
              disponibles. Sur chaque fiche, vous pourrez consulter les
              informations, <strong>acheter</strong> ou{" "}
              <strong>nous contacter directement via WhatsApp</strong>.
            </p>
          </div>

          <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
            {loadingCategories &&
              Array.from({ length: 4 }).map((_, idx) => (
                <CategorySkeletonCard key={idx} />
              ))}

            {!loadingCategories &&
              categories.map((cat) => (
                <div key={cat.id} className="col">
                  <a
                    href={`/categorie/${cat.slug}`}
                    className="text-decoration-none text-dark h-100 d-block"
                  >
                    <div className="card h-100 border-0 shadow-sm small rounded-4 hover-raise overflow-hidden bg-white">
                      <div className="position-relative">
                        <div className="ratio ratio-4x3 bg-light overflow-hidden">
                          {cat.image ? (
                            <img
                              src={cat.image}
                              alt={cat.name}
                              className="w-100 h-100"
                              style={{ objectFit: "cover" }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center h-100">
                              <span className="fw-bold text-muted text-center px-2">
                                {cat.name}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-50 text-white text-center py-1">
                          <span className="small fw-semibold">
                            {cat.name}
                            {cat.productCount > 0 && (
                              <span className="ms-1">
                                ({cat.productCount})
                              </span>
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="card-body text-center d-flex flex-column">
                        <p className="small text-muted mb-2 flex-grow-1">
                          {cat.description}
                        </p>
                        <div className="d-flex justify-content-center">
                          <span className="btn btn-sm btn-outline-dark rounded-pill px-3">
                            Voir les produits
                          </span>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              ))}

            {!loadingCategories && categories.length === 0 && (
              <div className="col-12">
                <div className="alert alert-light small mb-0 text-center">
                  Aucune catégorie détectée pour l’instant. Ajoutez des produits
                  dans <code>products_prosoft</code> avec un{" "}
                  <code>categorySlug</code> pour les voir ici.
                </div>
              </div>
            )}
          </div>
        </div>
      </section> */}

      {/* ✅ SECTION MEILLEURES OFFRES (Firestore) */}
      <section className="py-4 bg-white">
        <div className="container">
          {/* Titre avec lignes de part et d’autre */}
          <div className="d-flex align-items-center mb-3">
            <div className="flex-grow-1 border-top" />
            <div className="mx-3 text-center">
              <h5 className="fw-bold text-uppercase mb-1">Matériels et équipements disponibles</h5>
              <p className="small text-muted mb-0">
                Découvrez notre sélection de produits en vedette.
              </p>
            </div>
            <div className="flex-grow-1 border-top" />
          </div>

          {loadingOffers ? (
            <div className="row row-cols-2 row-cols-md-4 g-3 mt-1">
              {Array.from({ length: 4 }).map((_, idx) => (
                <BestOfferSkeletonCard key={idx} />
              ))}
            </div>
          ) : bestOffers.length === 0 ? (
            <p className="text-center text-muted small mb-0">
              Aucune offre en avant pour le moment.
            </p>
          ) : (
            <div className="row row-cols-2 row-cols-md-4 g-3 mt-1">
              {bestOffers.map((p) => {
                const imageSrc =
                  p.mainImage ||
                  (Array.isArray(p.images) && p.images[0]) ||
                  "/images/placeholder-product.jpg";

                const hasPrice = p.price != null;

                return (
                  <div key={p.id} className="col">
                    <a
                      href={`/produit/${p.slug || p.id}`}
                      className="text-decoration-none text-dark h-100 d-block"
                    >
                      <div className="ps-product-card h-100 d-flex flex-column rounded-4 border-0 shadow-sm overflow-hidden bg-white">
                        {/* Image + badge */}
                        <div className="position-relative">
                          <div className="ps-product-image">
                            <img src={imageSrc} alt={p.name} />
                          </div>
                          {p.oldPrice && hasPrice && (
                            <span className="badge bg-danger position-absolute top-0 start-0 m-2">
                              Promo
                            </span>
                          )}
                          {p.isFeatured && (
                            <span className="badge bg-warning text-dark position-absolute top-0 end-0 m-2">
                              En vedette
                            </span>
                          )}
                        </div>

                        {/* Corps */}
                        <div className="px-2 pb-2 pt-2 d-flex flex-column flex-grow-1">
                          <div className="ps-product-name small mb-1">
                            {p.name}
                          </div>

                          {p.brand && (
                            <div className="small mb-1">
                              <span className="badge bg-light text-dark rounded-pill">
                                {p.brand}
                              </span>
                            </div>
                          )}

                          <div className="mt-auto">
                            {hasPrice ? (
                              <div className="d-flex flex-column align-items-start mb-1">
                                <div className="d-flex align-items-baseline gap-1">
                                  <span className="fw-bold text-primary">
                                    {formatPrice(p.price)}
                                  </span>
                                  <span className="small text-muted">CFA</span>
                                </div>
                                {p.oldPrice && (
                                  <div className="small text-muted text-decoration-line-through">
                                    {formatPrice(p.oldPrice)} CFA
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="small fw-semibold text-secondary">
                                Prix sur demande
                              </span>
                            )}

                            {p.inStock ? (
                              <span className="badge bg-success-subtle text-success border border-success-subtle small">
                                En stock
                              </span>
                            ) : (
                              <span className="badge bg-danger-subtle text-danger border border-danger-subtle small">
                                Rupture
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default HomePage;

